/*eslint max-lines-per-function: ["error", 320]*/
import Select from 'react-select';
import React, { useEffect, useState } from 'react';
import { Form, Card, Button } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import {
  DrivingLicenseCategory,
  Healthcare,
  OccupationalMedicineCategory,
} from '../../../common/spdCore/validation/schemas';
import { CreateUpdateExamWithTestsDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import HealthcareFacilitySelector from '../Selects/HealthcareFacilitySelector';
import { FormSelect } from '../../../app/components/FormSelect';
import { TextWithDictionarySelector } from '../../../app/components/TextWithDictionarySelector';

import { AddExamConsultations } from './AddExamConsultations';

const EXAM_DESCRIPTION = 'Opis badania';

interface Props {
  prevStep?: () => void;
  nextStep?: () => void;
  handleFormData?: (input: string) => (s: string | boolean | Date) => void;
  drivingLicense: string | null | undefined;
  occupMedicineText: string | null | undefined;
  occupMedicineCategory: string[] | null | undefined;
  description: string | null | undefined;
  completed?: boolean | null | undefined;
  neurologist?: boolean | null | undefined;
  ophthalmologist?: boolean | null | undefined;
  laryngologist?: boolean | null | undefined;
  psychiatrist?: boolean | null | undefined;
  psychologistConsultation?: boolean | null | undefined;
  healthcareFacilityId?: string | null | undefined;
  healthcare?: Healthcare;
  alarm?: boolean | null | undefined;
  inFacility?: boolean | null | undefined;
  onChange?: (testsKeys: string[]) => void;
  propertyChange?: (obj: Partial<CreateUpdateExamWithTestsDto>) => void;
}

export const AddDescription: React.FC<Props> = ({
  prevStep,
  nextStep,
  handleFormData,
  drivingLicense,
  occupMedicineText,
  occupMedicineCategory,
  description,
  completed,
  neurologist,
  ophthalmologist,
  laryngologist,
  psychiatrist,
  psychologistConsultation,
  healthcareFacilityId,
  healthcare,
  alarm,
  inFacility,
  propertyChange,
}) => {
  const [selectedDrivingCategories, setSelectedDrivingCategories] = useState<
    string[]
  >(drivingLicense?.split(',') || []);
  const [selectedOccupMedicineCategory, setSelectedOccupMedicineCategory] =
    useState<string[]>(occupMedicineCategory || []);

  useEffect(() => {
    setSelectedDrivingCategories(drivingLicense?.split(',') || []);
    setSelectedOccupMedicineCategory(occupMedicineCategory || []);
  }, [drivingLicense, occupMedicineCategory]);

  const handleOnNext = () => {
    if (nextStep) {
      nextStep();
    }
  };

  const handleOnPrev = () => {
    if (prevStep) {
      prevStep();
    }
  };

  const propertyDescriptionChange = (desc: string) => {
    if (handleFormData) {
      handleFormData('description')(desc);
    }
    if (propertyChange) {
      propertyChange({ description: desc });
    }
  };

  const propertyCompletedChange = (com: boolean) => {
    if (propertyChange) {
      propertyChange({ completed: com });
    }
  };

  const propertyAlarmChange = (al: boolean) => {
    if (propertyChange) {
      propertyChange({ alarm: al });
    }
  };

  const propertyInFacilityChange = (val: boolean) => {
    if (handleFormData) {
      handleFormData('inFacility')(val);
    }
    if (propertyChange) {
      propertyChange({ inFacility: val });
    }
  };

  const propertyOccupMedicineTextChange = (occupMedText: string) => {
    if (handleFormData) {
      handleFormData('occupMedicineText')(occupMedText);
    }
    if (propertyChange) {
      propertyChange({ occupationalMedicineText: occupMedText });
    }
  };

  const propertyHealthcareFacilityIdChange = (healthcareFacilityId: string) => {
    if (handleFormData) {
      handleFormData('healthcareFacilityId')(healthcareFacilityId);
    }
    if (propertyChange) {
      propertyChange({ healthcareFacilityId: healthcareFacilityId });
    }
  };

  const onSelectDrivingCategoriesChange = (keys: string[]) => {
    setSelectedDrivingCategories(keys);
    if (handleFormData) {
      handleFormData('drivingLicense')(keys.join(','));
    }
    if (propertyChange) {
      propertyChange({ drivingLicense: keys.join(',') });
    }
  };

  const propertyChangeWithHandleFormData = (
    field: string,
    checked: boolean | string | Date,
  ) => {
    if (handleFormData) {
      handleFormData(field)(checked);
    }
    if (propertyChange) {
      propertyChange({ [field]: checked });
    }
  };

  const getAllDrivingCategories = () => {
    const arr = Object.keys(DrivingLicenseCategory).map(name => {
      return {
        value: name,
        label:
          DrivingLicenseCategory[name as keyof typeof DrivingLicenseCategory],
      };
    });
    return arr;
  };

  const getSelectedDrivingCategories = () => {
    return getAllDrivingCategories().filter(item =>
      selectedDrivingCategories?.includes(item.value),
    );
  };

  const onSelectOccupMedCategories = (keys: string[]) => {
    setSelectedOccupMedicineCategory(keys);
    if (handleFormData) {
      handleFormData('occupMedicineCategory')(keys.join(','));
    }
    if (propertyChange) {
      propertyChange({ occupationalMedicineCategory: keys });
    }
  };

  const getAllOccupMedCategories = () => {
    const arr = Object.keys(OccupationalMedicineCategory).map(name => {
      return {
        value: name,
        label:
          OccupationalMedicineCategory[
            name as keyof typeof OccupationalMedicineCategory
          ],
      };
    });
    return arr;
  };

  const getSelectedOccupMedCategories = () => {
    return getAllOccupMedCategories().filter(item =>
      selectedOccupMedicineCategory?.includes(item.value),
    );
  };

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Subtitle className="mb-4">Dane ogólne</Card.Subtitle>

          <Form className="d-grid gap-3">
            {!nextStep && (
              <>
                <FormRow controlId="alarm" label="Wyłączenie prośby o pomoc">
                  <Form.Check
                    type="checkbox"
                    checked={alarm ? true : false}
                    onChange={e => propertyAlarmChange(e.target.checked)}
                  />
                </FormRow>
                <FormRow
                  controlId="drivingCategories"
                  label="Dotyczy kategorii prawa jazdy"
                >
                  <Select
                    options={getAllDrivingCategories()}
                    value={getSelectedDrivingCategories()}
                    isMulti
                    className="test-multi-select"
                    classNamePrefix="driving-select"
                    onChange={options =>
                      onSelectDrivingCategoriesChange(options.map(o => o.value))
                    }
                  />
                </FormRow>
                <FormRow
                  controlId="occupMedCategories"
                  label="Dotyczy medycyny pracy"
                >
                  <Select
                    options={getAllOccupMedCategories()}
                    value={getSelectedOccupMedCategories()}
                    isMulti
                    className="test-multi-select"
                    classNamePrefix="driving-select"
                    onChange={options =>
                      onSelectOccupMedCategories(options.map(o => o.value))
                    }
                  />
                </FormRow>
                <FormRow controlId="occupMedText" label="Medycyna pracy - inne">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={occupMedicineText || ''}
                    onChange={e =>
                      propertyOccupMedicineTextChange(e.target.value)
                    }
                  />
                </FormRow>
              </>
            )}
            <FormRow controlId="healthcareFacility" label="Placówka">
              <HealthcareFacilitySelector
                value={healthcareFacilityId || ''}
                onChange={option => {
                  propertyHealthcareFacilityIdChange(option.value);
                }}
              />
            </FormRow>
            <FormRow
              controlId="inFacility"
              label="Badanie odbywa się w placówce"
            >
              <Form.Check
                type="checkbox"
                checked={inFacility ? true : false}
                onChange={e => propertyInFacilityChange(e.target.checked)}
              />
            </FormRow>
            <FormRow controlId="exam_description" label="Opis badania">
              {/* <Form.Control
                as="textarea"
                rows={3}
                value={description || ''}
                onChange={e => propertyDescriptionChange(e.target.value)}
              /> */}
              <TextWithDictionarySelector
                value={description || ''}
                onChange={propertyDescriptionChange}
                dictionaryType={EXAM_DESCRIPTION}
              />
            </FormRow>
            <FormRow controlId="healhcare" label="Medyk">
              <FormSelect
                value={healthcare}
                options={[
                  { value: 'Doctor', label: 'Lekarz/Lekarka' },
                  { value: 'Dentist', label: 'Stomatolog' },
                  { value: 'Midwife', label: 'Położna' },
                  { value: 'Nurse', label: 'Pielęgniarz/Pielęgniarka' },
                ]}
                onChange={value =>
                  propertyChangeWithHandleFormData('healthcare', value)
                }
              />
            </FormRow>
            <AddExamConsultations
              handleFormData={handleFormData}
              neurologist={neurologist}
              ophthalmologist={ophthalmologist}
              laryngologist={laryngologist}
              psychiatrist={psychiatrist}
              psychologistConsultation={psychologistConsultation}
              propertyChange={propertyChange}
            />
            {!prevStep && (
              <FormRow
                controlId="exam_completed"
                label="Wystawiono orzeczenia poza systemem"
              >
                <Form.Check
                  type="checkbox"
                  checked={completed ? true : false}
                  onChange={e => propertyCompletedChange(e.target.checked)}
                />
              </FormRow>
            )}

            {prevStep && nextStep && (
              <div className="d-flex justify-content-end m-3">
                <Button
                  variant="outline-secondary"
                  className="ms-1"
                  onClick={handleOnPrev}
                >
                  Wstecz
                </Button>

                <Button
                  variant="outline-primary"
                  className="ms-1"
                  onClick={handleOnNext}
                >
                  Dalej
                </Button>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};
